import React from 'react'

import { SFSymbolLabel, Tooltip } from '@sketch/components'
import { routes } from '@sketch/modules-common'
import { FrameGroupDetailsFragment } from '@sketch/gql-types'

import { useGetComponentsGroups } from '../operations'
import { countRealPathSegments } from '../utils'
import { formatSymbolPath } from '@sketch/inspector'
import { SymbolPathLinkDropdown } from '../../components/SymbolPathLinkDropdown'
import type NavigationItem from '../../components/HeaderNavigation/NavigationItem'

type CWVBreadcrumb = React.ComponentProps<typeof NavigationItem>

export interface UseCWVBreadcrumbsResult {
  breadcrumbs: CWVBreadcrumb[]
  // Whether the current frame's name was folded into a collapsed breadcrumb
  // label, so the caller shouldn't also render a separate frame-name crumb.
  mergedFrameName: boolean
}

const EMPTY_RESULT: UseCWVBreadcrumbsResult = {
  breadcrumbs: [],
  mergedFrameName: false,
}

/**
 * Builds the "Components / <group> / <symbol>" breadcrumb trail shown when
 * navigating into a symbol frame from the Symbols list.
 *
 * A symbol's own `component.path`/`depth` aren't reliable group indicators:
 * for a variant frame they hold the variant-selector values (e.g.
 * "Primary/Enabled"), and for a parentless (depth 0) symbol `path` is set to
 * the symbol's own name. Cross-referencing the real group tree tells us how
 * many leading path segments are real, navigable groups - everything after
 * that, together with the frame's name, collapses into one static label
 * instead of a chain of dead links.
 */
export const useCWVBreadcrumbs = (
  shareIdentifier: string,
  isMobile: boolean,
  enabled: boolean,
  frameGroup?: FrameGroupDetailsFragment
): UseCWVBreadcrumbsResult => {
  const shouldFetch = enabled && !!frameGroup?.component

  const { tree } = useGetComponentsGroups(shouldFetch ? 'SYMBOL' : undefined)

  if (!shouldFetch) {
    return EMPTY_RESULT
  }

  const depth = frameGroup?.component?.depth || 0
  const rawPath = depth > 0 ? frameGroup?.component?.path || '' : ''
  const paths = rawPath ? rawPath.split('/') : []
  const frameName = frameGroup?.component?.name || ''

  const realPathsLength = countRealPathSegments(paths, tree)
  const realPaths = paths.slice(0, realPathsLength)
  const fakePaths = paths.slice(realPathsLength)
  const mergedFrameName = fakePaths.length > 0

  // The fake segments aren't navigable groups, so - together with the
  // current frame's name - they're collapsed into one static label instead
  // of a chain of dead links.
  const collapsedLabel = [...fakePaths, frameName].filter(Boolean).join('/')

  const collapsedCrumb: CWVBreadcrumb[] = mergedFrameName
    ? [{ icon: 'symbol', label: <SFSymbolLabel text={collapsedLabel} /> }]
    : []

  if (isMobile) {
    // Given the mobile breakpoint restricts the header space we condense
    // everything into a single "..." dropdown.
    return {
      breadcrumbs: [
        {
          dropdownComponent: SymbolPathLinkDropdown,
          dropdownComponentProps: { paths: realPaths },
          hideDropdownChevron: true,
          label: '...',
        },
        ...collapsedCrumb,
      ],
      mergedFrameName,
    }
  }

  const breadcrumbs: CWVBreadcrumb[] = [
    {
      to: routes.CWV_SYMBOLS.create({ shareID: shareIdentifier }),
      icon: 'components',
      label: 'Components',
      collapsed: true,
    },
  ]

  if (realPaths.length > 0) {
    const [firstPath, ...middlePaths] = realPaths
    const [lastPath] = middlePaths.splice(-1)

    if (firstPath) {
      breadcrumbs.push({
        to: routes.CWV_SYMBOLS.create({
          shareID: shareIdentifier,
          query: {
            g: encodeURIComponent(firstPath),
          },
        }),
        label: <SFSymbolLabel text={firstPath} />,
      })
    }

    if (middlePaths.length === 1) {
      breadcrumbs.push({
        to: routes.CWV_SYMBOLS.create({
          shareID: shareIdentifier,
          query: {
            g: encodeURIComponent([firstPath, middlePaths[0]].join('/')),
          },
        }),
        label: <SFSymbolLabel text={middlePaths[0]} />,
      })
    } else if (middlePaths.length > 1) {
      const pathsWithSpace = middlePaths.map((path, index) => (
        <div key={path}>{formatSymbolPath(path, index)}</div>
      ))

      breadcrumbs.push({
        label: (
          <Tooltip
            content={pathsWithSpace}
            contentStyle={{ whiteSpace: 'pre' }}
          >
            ...
          </Tooltip>
        ),
        dropdownComponent: SymbolPathLinkDropdown,
        dropdownComponentProps: { paths: middlePaths, prefix: firstPath },
        dropdownProps: { placement: 'bottom-start' },
        hideDropdownChevron: true,
      })
    }

    if (lastPath) {
      breadcrumbs.push({
        to: routes.CWV_SYMBOLS.create({
          shareID: shareIdentifier,
          query: {
            g: encodeURIComponent(realPaths.join('/')),
          },
        }),
        label: <SFSymbolLabel text={lastPath} />,
      })
    }
  }

  breadcrumbs.push(...collapsedCrumb)

  return { breadcrumbs, mergedFrameName }
}
